import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface HealthIssue {
  id: number;
  signature: string;
  category: string;
  severity: string;
  title: string;
  detail: string;
  status: string; // open | self_healed
}

interface BannerState {
  sleptUntil: number;        // ms epoch; banner suppressed until then
  dismissedSigs: string[];   // signatures closed "until next new issue"
}

const STORAGE_KEY = 'exposiq.healthBanner';

function readState(): BannerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { sleptUntil: 0, dismissedSigs: [] };
}
function writeState(s: BannerState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

// SystemHealthBanner shows a top-of-page banner (admins only) when the self-monitoring
// reconciler has OPEN issues. "Sleep" hides it for a day; "Close" hides it until a NEW issue
// appears. Resolved issues are pruned so a recurrence re-surfaces.
const SystemHealthBanner: React.FC = () => {
  const [issues, setIssues] = useState<HealthIssue[]>([]);
  const [state, setState] = useState<BannerState>(readState());

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/health/issues');
      const list: HealthIssue[] = res.data?.issues || [];
      setIssues(list);
      // Prune dismissed signatures that no longer exist (so a recurrence re-shows).
      setState((prev) => {
        const live = new Set(list.map((i) => i.signature));
        const pruned = prev.dismissedSigs.filter((s) => live.has(s));
        if (pruned.length !== prev.dismissedSigs.length) {
          const next = { ...prev, dismissedSigs: pruned };
          writeState(next);
          return next;
        }
        return prev;
      });
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Only OPEN issues (not self_healed) are actionable for the banner.
  const openIssues = issues.filter((i) => i.status === 'open');
  const dismissed = new Set(state.dismissedSigs);
  const visibleIssues = openIssues.filter((i) => !dismissed.has(i.signature));
  const slept = Date.now() < state.sleptUntil;

  if (slept || visibleIssues.length === 0) return null;

  // Highest-severity issue leads.
  const order: Record<string, number> = { critical: 0, high: 1, medium: 2, info: 3 };
  const top = [...visibleIssues].sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9))[0];
  const sev = top.severity;
  const bg = sev === 'critical' ? 'rgba(239,68,68,0.12)' : sev === 'high' ? 'rgba(249,115,22,0.12)' : sev === 'medium' ? 'rgba(234,179,8,0.12)' : 'rgba(99,102,241,0.12)';
  const bd = sev === 'critical' ? '#ef4444' : sev === 'high' ? '#f97316' : sev === 'medium' ? '#eab308' : '#6366f1';

  const sleep = () => { const n = { ...state, sleptUntil: Date.now() + 24 * 3600 * 1000 }; setState(n); writeState(n); };
  const close = () => { const sigs = Array.from(new Set([...state.dismissedSigs, ...openIssues.map((i) => i.signature)])); const n = { ...state, dismissedSigs: sigs }; setState(n); writeState(n); };

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', marginBottom: 16,
        background: bg, border: `1px solid ${bd}`, borderLeft: `4px solid ${bd}`, borderRadius: 8,
      }}
    >
      <i className="fas fa-heart-pulse" style={{ color: bd, fontSize: '1.1rem' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {top.title}
          {visibleIssues.length > 1 && (
            <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--color-text-tertiary)' }}>
              +{visibleIssues.length - 1} weitere
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {top.detail}
        </div>
      </div>
      <Link to="/admin/health" style={{ whiteSpace: 'nowrap', fontWeight: 600, color: bd, textDecoration: 'none' }}>
        Details →
      </Link>
      <button onClick={sleep} title="Für einen Tag ausblenden" style={btnStyle}>Sleep</button>
      <button onClick={close} title="Ausblenden bis zur nächsten neuen Meldung" style={btnStyle}>Close</button>
    </div>
  );
};

const btnStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--color-border, #2a3348)', color: 'var(--color-text-secondary)',
  borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap',
};

export default SystemHealthBanner;
